/**
 * RLQ — LobbyEntryKiosk
 *
 * Kiosk-mode self check-in. Branch-tablet UI with extra-large tap targets
 * and a 3-step flow:
 *   1. Tell us who you are (existing customer hint OR first/last name).
 *   2. Pick a reason — icon picker grid.
 *   3. Confirmation screen — "You're in line".
 *
 * The legacy `KioskCheckIn.tsx` covered the data plumbing; this is the
 * modernized layout. We import its `ServiceTypePicker` (no duplicate
 * config-loading logic) and reuse the `client.retail.sessions.create` path.
 *
 * Role-gate (handled in `routes.tsx`):
 *   - Allowed when `user.primaryRole === 'Kiosk'` OR `?kiosk=1` query
 *     param is present (dev/demo).
 */

import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  CheckCircle2,
  Search,
  Users,
} from '@retail-ops/panel-shell/fi';

import { Button, Pill, Stepper } from '@/components/primitives';
import type { StepperStep } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { asIso8601 } from '@retail-ops/shared-types';
import type { SessionEventPayload } from '@retail-ops/shared-types';

import { ServiceTypePicker, useServiceTypes } from '../components/ServiceTypePicker';

const STEPS: StepperStep[] = [
  { label: 'You' },
  { label: 'Reason' },
  { label: 'Done' },
];

type KioskStep = 0 | 1 | 2;
type CustomerKind = 'existing' | 'new';

interface SubmitState {
  submitting: boolean;
  error: string | null;
  reservedAt: Date | null;
}

export default function LobbyEntryKiosk(): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const services = useServiceTypes();

  const [step, setStep] = useState<KioskStep>(0);
  const [kind, setKind] = useState<CustomerKind>('existing');
  const [identifier, setIdentifier] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [serviceCode, setServiceCode] = useState<string | null>(null);
  const [submit, setSubmit] = useState<SubmitState>({
    submitting: false,
    error: null,
    reservedAt: null,
  });

  const selectedService = useMemo(
    () => services.find((s) => s.code === serviceCode) ?? null,
    [services, serviceCode],
  );

  const customerOk =
    kind === 'existing'
      ? identifier.trim().length >= 4
      : firstName.trim().length > 0 && lastName.trim().length > 0;

  const reset = (): void => {
    setStep(0);
    setKind('existing');
    setIdentifier('');
    setFirstName('');
    setLastName('');
    setServiceCode(null);
    setSubmit({ submitting: false, error: null, reservedAt: null });
  };

  const handleJoin = async (): Promise<void> => {
    if (!selectedService || !customerOk) return;
    setSubmit({ submitting: true, error: null, reservedAt: null });
    try {
      const res = await client.retail.sessions.create({
        officerCode: 'KIOSK',
        ...(user?.upn ? { officerEmail: user.upn } : {}),
        ...(user?.displayName ? { officerName: user.displayName } : {}),
        branch: user?.activeBranchCode ?? user?.branchCode ?? '1',
        sessionType: 'lobby',
        serviceType: selectedService.code,
        origin: 'kiosk',
      });

      const sessionId = res.session?.sessionId;
      if (sessionId) {
        const payload: SessionEventPayload = {
          sessionId,
          eventType: 'queue-entry-created',
          eventCategory: 'session',
          severity: 'info',
          actor: user?.upn ?? 'kiosk',
          timestamp: asIso8601(new Date().toISOString()),
          payloadJson: JSON.stringify({
            entryChannel: 'kiosk',
            serviceType: selectedService.code,
            requiredPermission: selectedService.requiredPermission,
            targetModule: selectedService.targetModule,
            customer:
              kind === 'existing'
                ? { kind: 'existing', maskedIdentifier: maskIdentifier(identifier) }
                : { kind: 'new', firstName, lastName },
          }),
        };
        client.retail.events.write(payload).catch(() => {
          // non-blocking
        });
      }

      setSubmit({
        submitting: false,
        error: sessionId ? null : 'Check-in created but no reference returned.',
        reservedAt: new Date(),
      });
      setStep(2);
    } catch (err: unknown) {
      setSubmit({
        submitting: false,
        error: err instanceof Error ? err.message : 'Check-in failed. Please tell a banker.',
        reservedAt: null,
      });
    }
  };

  // -------------------------------------------------------------------------
  // STEP 2 — confirmation
  // -------------------------------------------------------------------------
  if (step === 2 && submit.reservedAt) {
    return (
      <div className="lobby-page lobby-kiosk">
        <Stepper steps={STEPS} currentIndex={2} variant="booking" />
        <div className="lobby-kiosk__confirmation">
          <div className="lobby-kiosk__confirmation-icon" aria-hidden>
            <CheckCircle2 size={56} />
          </div>
          <h1 className="lobby-kiosk__confirmation-title">You're in line</h1>
          <p className="lobby-kiosk__confirmation-subtitle">
            A banker will be with you shortly. You may have a seat.
          </p>
          <dl className="lobby-kiosk__summary">
            <div>
              <dt>Reason</dt>
              <dd>{selectedService?.label ?? '—'}</dd>
            </div>
            <div>
              <dt>Checked in</dt>
              <dd>
                {submit.reservedAt.toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                })}
              </dd>
            </div>
          </dl>
          <Button variant="primary" size="lg" onClick={reset}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // STEP 0 — Identity
  // -------------------------------------------------------------------------
  if (step === 0) {
    return (
      <div className="lobby-page lobby-kiosk">
        <Stepper steps={STEPS} currentIndex={0} variant="booking" />
        <div className="lobby-kiosk__panel">
          <h1 className="lobby-kiosk__title">Welcome to PCB</h1>
          <p className="lobby-kiosk__subtitle">Tell us who you are.</p>

          <div className="lobby-kiosk__choice-grid">
            <button
              type="button"
              className={`lobby-kiosk__choice ${kind === 'existing' ? 'is-selected' : ''}`}
              onClick={() => setKind('existing')}
            >
              <span className="lobby-kiosk__choice-icon" aria-hidden>
                <Users size={28} />
              </span>
              <span className="lobby-kiosk__choice-title">I bank here</span>
              <span className="lobby-kiosk__choice-helper">Use last 4 of SSN, phone, or account</span>
            </button>
            <button
              type="button"
              className={`lobby-kiosk__choice ${kind === 'new' ? 'is-selected' : ''}`}
              onClick={() => setKind('new')}
            >
              <span className="lobby-kiosk__choice-icon" aria-hidden>
                <Users size={28} />
              </span>
              <span className="lobby-kiosk__choice-title">I'm new</span>
              <span className="lobby-kiosk__choice-helper">Tell us your name</span>
            </button>
          </div>

          {kind === 'existing' ? (
            <label className="lobby-kiosk__field">
              <span className="lobby-kiosk__field-label">Last 4 of SSN, phone, or account</span>
              <span className="lobby-kiosk__field-control">
                <Search size={20} aria-hidden />
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  maxLength={16}
                />
              </span>
            </label>
          ) : (
            <div className="lobby-kiosk__name-grid">
              <label className="lobby-kiosk__field">
                <span className="lobby-kiosk__field-label">First name</span>
                <span className="lobby-kiosk__field-control">
                  <input
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    maxLength={40}
                  />
                </span>
              </label>
              <label className="lobby-kiosk__field">
                <span className="lobby-kiosk__field-label">Last name</span>
                <span className="lobby-kiosk__field-control">
                  <input
                    type="text"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    maxLength={40}
                  />
                </span>
              </label>
            </div>
          )}
        </div>

        <div className="lobby-kiosk__actions">
          <Button
            variant="primary"
            size="lg"
            disabled={!customerOk}
            onClick={() => setStep(1)}
          >
            Continue →
          </Button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // STEP 1 — Reason
  // -------------------------------------------------------------------------
  return (
    <div className="lobby-page lobby-kiosk">
      <Stepper steps={STEPS} currentIndex={1} variant="booking" />
      <div className="lobby-kiosk__panel">
        <h1 className="lobby-kiosk__title">What brings you in today?</h1>
        <p className="lobby-kiosk__subtitle">Pick the closest match.</p>
        <ServiceTypePicker
          selected={serviceCode}
          onChange={(code) => setServiceCode(code)}
        />
      </div>

      {submit.error && (
        <div role="alert" className="lobby-kiosk__error">
          <Pill variant="err">{submit.error}</Pill>
        </div>
      )}

      <div className="lobby-kiosk__actions">
        <Button
          variant="ghost"
          size="lg"
          startIcon={<ArrowLeft size={16} />}
          onClick={() => setStep(0)}
        >
          Back
        </Button>
        <Button
          variant="primary"
          size="lg"
          disabled={!serviceCode || submit.submitting}
          onClick={handleJoin}
        >
          {submit.submitting ? 'Joining…' : 'Join the line'}
        </Button>
      </div>
    </div>
  );
}

function maskIdentifier(input: string): string {
  const clean = input.trim();
  if (clean.length <= 4) return `****${clean}`;
  return `${'*'.repeat(Math.max(0, clean.length - 4))}${clean.slice(-4)}`;
}
